import { useQuery } from '@apollo/client'
import { Col, Row, Separator } from '@farol-ds/react'
import { Footer } from '@jusbrasil/footer'
import { Topbar, withTopbarProps } from '@jusbrasil/topbar'
import { withApollo } from '@jusbrasil-web/core-apollo'
import {
  DocSearch,
  DocSearchElement,
  DocumentStatus,
  redirectDocumentUrl,
} from '@jusbrasil-web/docview/core'
import { useGA4Dispatcher } from '@jusbrasil-web/shared-ga4'
import {
  COMPONENT_CLASS,
  COMPONENT_NAME,
  GA4ClickEvent,
  SELECTED_OPTION,
} from '@jusbrasil-web/shared-ga4-events'
import { withUserSession } from '@jusbrasil-web/shared-user-session'
import { GetServerSideProps, InferGetServerSidePropsType } from 'next'
import { useRef } from 'react'

import {
  BlogBanner,
  BlogDocument,
  BlogHead,
  BlogHeader,
  JusiaProductTopbar,
} from '../../components'
import { BlogTags } from '../../components/blog-tags'
import { extractFromUrl } from '../../utils'
import { BlogRootQuery, BlogRootQueryVariables } from './__generated__/query.graphql'
import styles from './blog.module.scss'
import {
  BLOG_JUSTECH_BASE_URL,
  HIDDEN_TOPIC_ID,
  JUS_ACADEMY_BASE_URL,
  MANDATORY_JUS_ACADEMY_TOPIC_ID,
  MANDATORY_JUSTECH_BLOG_TOPIC_ID,
} from './constants'
import { BlogRoot } from './query'
import { SegmentationType } from './types'

export interface BlogPageProps {
  variables: BlogRootQueryVariables
  isJusAcademyPost?: boolean
}

export function BlogPage(props: InferGetServerSidePropsType<typeof getServerSideProps>) {
  const { variables, isJusAcademyPost } = props
  const contentRef = useRef<HTMLDivElement>(null)
  const docSearchRef = useRef<DocSearchElement>(null)
  const { sendGA4Event } = useGA4Dispatcher()

  const { data } = useQuery<BlogRootQuery, BlogRootQueryVariables>(BlogRoot, { variables })
  const document = data?.root?.document
  const viewer = data?.root?.viewer
  const publisher = document?.publisher ?? null
  const hasPaidPlan = !!viewer?.hasPaidPlan

  if (!document) return null

  const tags =
    document?.topics?.edges
      ?.filter(
        (edge) =>
          edge?.node?.tid !== MANDATORY_JUSTECH_BLOG_TOPIC_ID &&
          edge?.node?.tid !== MANDATORY_JUS_ACADEMY_TOPIC_ID &&
          edge?.node?.tid !== HIDDEN_TOPIC_ID
      )
      .map((edge) => edge?.node) || []

  const baseUrl = isJusAcademyPost ? '/jusia-academy' : '/blog'

  const { publicationSegmentation, title, subtitle, date, postBlogUrl } = document || {}
  const typeSegmentation = publicationSegmentation?.typeSegmentation as SegmentationType
  const isJusIAPost = typeSegmentation === 'PESQUISA_JURIDICA'
  const hasEmbeddedVideo = /data-media=(['"])embed-video\1/.test(document.text ?? '')

  const documentWithEditUrl =
    isJusAcademyPost && document.editUrl
      ? { ...document, editUrl: document.editUrl.replace('/blog/', '/jusia-academy/') }
      : document

  const onVideoClick = (src: string) => {
    sendGA4Event(
      new GA4ClickEvent({
        componentClass: COMPONENT_CLASS.BLOG_FEED,
        componentName: COMPONENT_NAME.BLOG_POST_ARTICLE,
        resourceId: `POST_BLOG:${document.docId}`,
        selectedOption: SELECTED_OPTION.BLOG_POST_VIDEO,
        contentSubgroup: src,
      })
    )
  }

  return (
    <>
      {isJusIAPost ? <JusiaProductTopbar /> : <Topbar variant="slim" />}
      {hasPaidPlan && (
        <DocSearch contentRef={contentRef} ref={docSearchRef} slug={document.slug || undefined} />
      )}
      <BlogHead
        title={`${title} | ${isJusAcademyPost ? 'Jus IA Academy' : 'Blog Justech'}`}
        blogLabel={isJusAcademyPost ? 'Jus IA Academy' : 'Blog'}
        canonicalUrl={postBlogUrl || ''}
        description={
          subtitle ||
          `No ${isJusAcademyPost ? 'Jus IA Academy' : 'Blog Justech'}, entenda mais sobre ${title}`
        }
        imageAlternativeText={title || ''}
        keywords={[]}
        hasEmbeddedVideo={hasEmbeddedVideo}
        structuredDataContext={{
          isJusAcademyPost: !!isJusAcademyPost,
          publishedAt: date,
          publisher,
          articleBody: document.text,
        }}
      />
      <main className={styles.main}>
        <BlogHeader
          title={title || ''}
          subtitle={subtitle || ''}
          publishDate={date || 0}
          isJusIAPost={isJusIAPost}
          publisher={publisher}
          document={documentWithEditUrl}
          viewer={viewer ?? null}
          docSearchRef={docSearchRef}
          breadcrumbHref={baseUrl}
          breadcrumbLabel={isJusAcademyPost ? 'Jus IA Academy' : 'Blog'}
        />
        <Row className={styles.blogContainer}>
          <Col xs={11} sm={9} md={8} lg={6}>
            <BlogDocument document={document} contentRef={contentRef} onVideoClick={onVideoClick} />
            <BlogTags tags={tags} baseUrl={baseUrl} />
            <Separator className={styles.separator} />
            <BlogBanner typeSegmentation={typeSegmentation} />
          </Col>
        </Row>
      </main>
      <Footer />
    </>
  )
}

const getServerSideProps: GetServerSideProps<BlogPageProps> = async (ctx) => {
  const { parts } = ctx.query
  if (!parts) {
    return { notFound: true }
  }

  const { docId, slug } = extractFromUrl(Array.isArray(parts) ? parts : [parts])
  if (!docId) {
    return { notFound: true }
  }

  const variables: BlogRootQueryVariables = { docId }
  const { client, withApolloProps } = withApollo(ctx)

  const { data } = await client.query<BlogRootQuery>({
    query: BlogRoot,
    variables,
  })

  const { document } = data?.root ?? {}
  if (!document) {
    return { notFound: true }
  }

  if (document.status === DocumentStatus.REMOVIDO) {
    ctx.res.statusCode = 410
    return {
      props: {
        variables,
        httpError: true,
      },
    }
  }
  if (!document.isActive) {
    ctx.res.statusCode = 401
    return {
      props: {
        variables,
        httpError: true,
      },
    }
  }

  const isOnJusAcademyRoute = ctx.resolvedUrl.startsWith('/jusia-academy')
  const isJusAcademyPost = Boolean(
    document.topics?.edges?.some((edge) => edge?.node?.tid === MANDATORY_JUS_ACADEMY_TOPIC_ID)
  )

  if (isJusAcademyPost !== isOnJusAcademyRoute) {
    const base = isJusAcademyPost ? JUS_ACADEMY_BASE_URL : BLOG_JUSTECH_BASE_URL
    const destination = slug ? `${base}/${slug}/${docId}` : `${base}/${docId}`
    return { redirect: { destination, permanent: false } }
  }

  const redirect = document.postBlogUrl && redirectDocumentUrl(ctx, document.postBlogUrl)
  if (redirect) {
    return { redirect }
  }

  return {
    props: {
      ...withApolloProps(),
      ...withTopbarProps(ctx),
      ...withUserSession(ctx),
      variables,
      isJusAcademyPost,
    },
  }
}

export const BlogPageExports = { getServerSideProps }
